import React from 'react';
import BrainLogo from '../components/BrainLogo';

/**
 * Terms of service.
 *
 * Deliberately short. A long terms page that nobody reads protects nobody; the
 * clauses that actually matter here are the medical disclaimer, the limits of
 * what the service claims to do, and who is responsible for the information
 * entered about another person.
 */

const EFFECTIVE = 'August 16, 2026';

export default function Terms({ navigate }) {
  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', color: '#b4b4b4' }}>
      <nav style={{
        maxWidth: 720, margin: '0 auto',
        padding: '22px 24px', display: 'flex', alignItems: 'center',
      }}>
        <button
          onClick={() => navigate('landing')}
          style={{
            display: 'flex', alignItems: 'center', gap: 9,
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          }}
        >
          <BrainLogo size={28} color="white" />
          <span style={{ color: '#ececec', fontWeight: 600, fontSize: 16, fontFamily: 'Inter, sans-serif' }}>
            Mindris
          </span>
        </button>
      </nav>

      <div className="manifesto-body" style={{
        maxWidth: 720, margin: '0 auto', padding: '16px 24px 110px',
        fontFamily: 'Inter, sans-serif', fontSize: 15.5, lineHeight: 1.85,
      }}>
        <h1 style={{
          fontSize: 'clamp(28px, 6vw, 40px)', fontWeight: 700, color: '#ffffff',
          letterSpacing: '-1px', fontFamily: 'Lora, Georgia, serif',
          margin: '24px 0 8px',
        }}>
          Terms of service
        </h1>
        <p style={{ color: '#6b6b6b', fontSize: 13, marginBottom: 40 }}>
          Effective {EFFECTIVE}
        </p>

        <p className="pull">
          Mindris gives caregivers suggestions. You decide what to do. That
          distinction is the whole basis of these terms.
        </p>

        <H>What Mindris is</H>
        <p>
          Mindris is a support tool for people caring for someone with a
          neurological or developmental condition. You build a profile, describe
          what is happening, and receive suggested steps based on what you have
          recorded and what has worked before.
        </p>

        <H>WHAT MINDRIS IS NOT</H>
        <p style={{ letterSpacing: '0.3px', lineHeight: 1.95 }}>
          MINDRIS IS NOT A MEDICAL DEVICE, AND IT DOES NOT PROVIDE MEDICAL
          ADVICE, DIAGNOSIS, OR TREATMENT. IT DOES NOT REPLACE A DOCTOR,
          THERAPIST, BEHAVIOUR SPECIALIST, OR EMERGENCY SERVICE. SUGGESTIONS ARE
          GENERATED BY AN AI SYSTEM AND CAN BE WRONG, INCOMPLETE, OR UNSUITABLE
          FOR YOUR SITUATION.
        </p>
        <p style={{ letterSpacing: '0.3px', lineHeight: 1.95 }}>
          YOU ARE RESPONSIBLE FOR EVERY DECISION YOU MAKE. USE YOUR OWN
          JUDGEMENT, AND CONSULT QUALIFIED PROFESSIONALS ABOUT THE PERSON YOU
          CARE FOR. IN AN EMERGENCY, CONTACT EMERGENCY SERVICES IMMEDIATELY.
        </p>

        <H>Your account</H>
        <p>
          You must be 18 or older to create an account. Keep your password to
          yourself; you are responsible for what happens under your account. Give
          us accurate information, and tell us if you believe someone else has
          access.
        </p>

        <H>Information about other people</H>
        <p>
          Most profiles describe someone other than you, often a minor. By
          entering that information you confirm you are their parent or legal
          guardian, or that you have permission from the person responsible for
          them.
        </p>

        <H>Acceptable use</H>
        <p>Don't use Mindris to:</p>
        <ul style={{ paddingLeft: 22, margin: '0 0 30px' }}>
          <li style={{ marginBottom: 8 }}>enter information about someone without the right to do so</li>
          <li style={{ marginBottom: 8 }}>attempt to access another account's data</li>
          <li style={{ marginBottom: 8 }}>disrupt the service or work around its limits</li>
          <li style={{ marginBottom: 8 }}>resell or redistribute the guidance as your own professional advice</li>
        </ul>

        <H>Availability</H>
        <p>
          Mindris is provided as-is. We do not guarantee it will be available
          when you need it, and we may change or discontinue features. We will
          give notice before any change that would remove access to your data.
        </p>

        <H>Cost</H>
        <p>
          Mindris is currently free. If we introduce paid features, existing
          functionality you rely on will not be moved behind a paywall without
          notice.
        </p>

        <H>Ending your account</H>
        <p>
          Delete your account at any time from Settings. It removes everything
          permanently. We may suspend accounts that violate these terms.
        </p>

        <H>Reporting a harmful or inappropriate suggestion</H>
        <p>
          Suggestions are generated by an AI system, and despite the safeguards
          in place one can still come back wrong, unsafe, or offensive. If that
          happens, mark the response as unhelpful in the app and email{' '}
          <a href="mailto:tylerkim1020@gmail.com" style={{ color: '#0a9c85', textDecoration: 'none' }}>
            tylerkim1020@gmail.com
          </a>{' '}
          with what you saw. Reports are reviewed and acted on, and repeated
          misuse of the service to generate harmful content will end an account.
        </p>

        <H>Liability</H>
        <p>
          To the fullest extent permitted by law, Mindris is not liable for
          decisions made using its suggestions, or for any resulting outcome.
          This does not limit liability that cannot legally be limited.
        </p>

        <H>Changes to these terms</H>
        <p>
          We may update these terms. If a change materially affects your rights
          or how you may use Mindris, we will tell you in the app before it
          takes effect. Continuing to use Mindris after that means you accept
          the updated terms. If you don't, you can delete your account.
        </p>

        <H>If you downloaded Mindris from the App Store</H>
        <p>
          These terms are between you and Mindris, not Apple. Apple is not
          responsible for Mindris or its content, and has no obligation to
          provide maintenance or support. If Mindris fails to conform to any
          applicable warranty, you may notify Apple and Apple will refund the
          purchase price, if any; to the maximum extent permitted by law, Apple
          has no other warranty obligation with respect to Mindris. Any claim
          relating to Mindris, including product liability, legal or regulatory
          non-compliance, or consumer protection claims, is our responsibility
          and not Apple's. Any third-party claim that Mindris infringes
          intellectual property rights is likewise our responsibility. You
          confirm you are not located in a country subject to a US Government
          embargo or designated as terrorist-supporting, and that you are not on
          any US Government restricted-parties list. Apple and its subsidiaries
          are third-party beneficiaries of these terms and may enforce them
          against you.
        </p>

        <H>Contact</H>
        <p>
          Questions about these terms:{' '}
          <a href="mailto:tylerkim1020@gmail.com" style={{ color: '#0a9c85', textDecoration: 'none' }}>
            tylerkim1020@gmail.com
          </a>
        </p>

        <div style={{ marginTop: 56, paddingTop: 24, borderTop: '1px solid #262626' }}>
          <button
            onClick={() => navigate('landing')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              color: '#6b6b6b', fontSize: 14, fontFamily: 'Inter, sans-serif',
            }}
          >
            ← Back to Mindris
          </button>
        </div>
      </div>
    </div>
  );
}

function H({ children }) {
  return (
    <h2 style={{
      fontSize: 19, fontWeight: 600, color: '#ececec',
      margin: '44px 0 14px', letterSpacing: '-0.3px',
      fontFamily: 'Inter, sans-serif',
    }}>{children}</h2>
  );
}
